/**
 * Tells an approver, on screen and in the browser, that access requests are waiting.
 * Polls the pending approvals every 30 s; a request seen for the first time raises a toast and,
 * if the person allowed it, a desktop notification. The tab title carries the pending count.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../hooks/usePermission';

const POLL_MS = 30_000;

const ApprovalNotifier: React.FC<{ apiUrl: string }> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const canDecide = usePermission('update:policy');
  const navigate = useNavigate();
  const seen = useRef<Set<string> | null>(null);
  const baseTitle = useRef(document.title);
  const [toast, setToast] = useState<{ count: number; first: string } | null>(null);
  const [asked, setAsked] = useState(() => typeof Notification === 'undefined' || Notification.permission !== 'default');

  useEffect(() => {
    if (!canDecide) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${apiUrl}/admin/approvals`);
        if (!r.ok || cancelled) return;
        const list: Array<{ id: string; kind: string; payload: any }> = (await r.json()).requests || [];
        const jit = list.filter((x) => x.kind === 'jit_target_access');
        document.title = jit.length ? `(${jit.length}) ${baseTitle.current}` : baseTitle.current;
        const ids = new Set(jit.map((x) => x.id));
        if (seen.current) {
          const fresh = jit.filter((x) => !seen.current!.has(x.id));
          if (fresh.length) {
            const first = fresh[0].payload?.requester?.displayName || fresh[0].payload?.userUpn || 'A user';
            setToast({ count: fresh.length, first });
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              new Notification(t('components.approvalNotifier.title', { defaultValue: 'Access request waiting' }), {
                body: `${first} → ${fresh[0].payload?.providerId || ''}`,
              });
            }
          }
        }
        seen.current = ids;
      } catch { /* offline or signed out: try again next time */ }
    };
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); document.title = baseTitle.current; };
  }, [apiUrl, canDecide, t]);

  if (!toast) return null;
  return (
    <div role="status" style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1200, maxWidth: 340, padding: 14, borderRadius: 10,
      background: 'var(--surface, #1b1f27)', color: 'var(--text, #fff)', border: '1px solid var(--border, #333)', boxShadow: '0 8px 24px rgba(0,0,0,.35)' }}>
      <strong>{t('components.approvalNotifier.title', { defaultValue: 'Access request waiting' })}</strong>
      <div style={{ margin: '6px 0 10px', fontSize: 13 }}>
        {toast.count > 1
          ? t('components.approvalNotifier.many', { defaultValue: '{{count}} new requests, starting with {{who}}.', count: toast.count, who: toast.first })
          : t('components.approvalNotifier.one', { defaultValue: '{{who}} is asking for access.', who: toast.first })}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="ent-btn sm primary" onClick={() => { setToast(null); navigate('/jit-access'); }}>
          {t('components.approvalNotifier.review', { defaultValue: 'Review' })}
        </button>
        <button className="ent-btn sm" onClick={() => setToast(null)}>{t('components.approvalNotifier.dismiss', { defaultValue: 'Dismiss' })}</button>
        {!asked && (
          <button className="ent-btn sm" onClick={() => { setAsked(true); void Notification.requestPermission(); }}>
            {t('components.approvalNotifier.enable', { defaultValue: 'Enable desktop alerts' })}
          </button>
        )}
      </div>
    </div>
  );
};

export default ApprovalNotifier;
